// AgentFacts — the ONE renderer for "what an agent contributed about this flat, and who".
//
// Owner ruling 2026-08-27, across four messages: *"whatever is shown must reflect who filled
// it"* … *"was it AI agent or another real agent"* … *"and who"* … *"we need to be consistent in
// our system"* … *"cards modals must reflect what was declared and by who"*.
//
// The card face and the property modal render the SAME rows through this component, from the
// same endpoint (`GET /api/core/property-chips`), wearing the same marks. That is the
// CooperationChips rule applied to a second kind of chip: one renderer, every surface, no
// per-surface bespoke logic — because the moment the card and the modal each grow their own
// version of "who said this", they start disagreeing about the same flat.
//
// TWO REGISTERS, ONE ROW:
//   • defects (group `defect`)       — a warning. Amber, ⚠ glyph. Something is WRONG.
//   • agent-filled (group `agent_filled`) — an ordinary fact whose SOURCE FIELD WAS EMPTY and
//     an agent filled. Plain text. What earns it a place on the face is not the value but the
//     mark beside it.
//
// ⚠️ "SOURCE FIELD", NOT "THE ADVERT NEVER SAID IT" — the distinction is real and I got it
// wrong first. The rule is "no `scrape` row for this code", and a `scrape` row is the PORTAL'S
// STRUCTURED FIELD. Idmon reads the advert's PROSE, so the very first real sample was a balcony
// carrying the quote „აქვს აივანი": the advert says it in words, and the portal's balcony
// checkbox is empty. That IS the owner's case — *"all fields empty from source and idmon
// extracts data, it takes over empty fields"* — but describing it as "the advert never said it"
// is false to anyone who then opens the advert and reads the sentence.
//
// ⚠️ ABSENCE DRAWS NOTHING, AND THERE IS NO "ALL CLEAR" STATE. For defects that is forced by
// the data: the promotion registry refuses to write `present=false` for those codes (migration
// 0745), because a photograph showing no damp is evidence about a wall, not about the flat. A
// green tick here would be a claim nobody made.
//
// ⚠️ `__more__` IS THE SERVER SAYING IT CAPPED THE LIST, and it renders. A card that shows six
// of nine and says nothing has told the agent the flat has six — the no-silent-caps rule.
import React from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { IconText } from '../Icon.jsx'
import AiMark, { sourceMarkFor } from './AiMark.jsx'

function labelOf(code, features) {
  const f = (features || []).find(x => x.code === code)
  return f?.name_ka || f?.name_en || String(code).replace(/_/g, ' ')
}

export function hasAgentFacts(defects, agentFacts) {
  return (defects?.length || 0) > 0 || (agentFacts?.length || 0) > 0
}

export default function AgentFacts({ defects = null, agentFacts = null, features = null, className = '' }) {
  if (!hasAgentFacts(defects, agentFacts)) return null
  return (
    <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 ${className}`}>
      {(defects || []).map(d => {
        if (d.code === '__more__') return <span key="dmore" className="text-xs text-muted-foreground">+{d.more}</span>
        const label = labelOf(d.code, features)
        // The frames travel with the claim, so "where it was seen" is checkable in one hover.
        // ⚠️ 1-BASED for the reader: `photo_index` is 0-based in the claim and an agent
        // counting photographs starts at one.
        const where = d.photos?.length ? tLog('desk_defect_frames', { frames: d.photos.map(n => n + 1).join(', ') }) : null
        return (
          <span key={d.code}
            className={'text-xs inline-flex items-center gap-1 ' + (d.own ? 'text-amber-600 dark:text-amber-500' : 'text-muted-foreground')}
            title={[label, where, d.own ? null : t('desk_defect_from_sibling')].filter(Boolean).join(' — ')}>
            {/* The leading glyph is what IconText maps to the alert-triangle; without it the
                chip renders as bare text and reads like an amenity. */}
            <IconText>{'⚠️ ' + label}</IconText>
            <AiMark mark={sourceMarkFor(d.sources)} />
          </span>
        )
      })}
      {(agentFacts || []).map(d => {
        if (d.code === '__more__') return <span key="amore" className="text-xs text-muted-foreground">+{d.more}</span>
        return (
          <span key={d.code} className="text-xs text-muted-foreground inline-flex items-center gap-1">
            {labelOf(d.code, features)}
            <AiMark mark={sourceMarkFor(d.sources)} />
          </span>
        )
      })}
    </div>
  )
}
